import type { ComponentType } from 'react';
import { SiDiscord, SiX, SiYoutube } from 'react-icons/si';
import { cn } from '@/lib/utils';

/**
 * "Connect with us" icon row — sourced from `react-icons` so we get the
 * official brand glyphs (Simple Icons + Font Awesome) without hand-rolling
 * SVGs and keeping them in sync with brand updates.
 *
 * Variants:
 * - `chips` (default): bordered circular icon chips, used at the bottom of
 *   the page where social is the call to action.
 * - `subtle`: borderless icons on a darker surface, used right under the
 *   hero CTA where the download button is the headline and social is a
 *   supporting funnel.
 *
 * Drop new platforms in via the `SOCIALS` array; entries with an empty
 * `href` are filtered out, so unconfirmed URLs can sit there until ready.
 */

type SocialLink = {
  label: string;
  href: string;
  /** A react-icons component (or any component that takes `className`). */
  Icon: ComponentType<{ className?: string }>;
};

// Edit this list to add/remove platforms. Entries with `href: ''` are
// filtered out at render time so unconfirmed URLs can stay parked here.
const SOCIALS: SocialLink[] = [
  {
    label: 'Join our Discord',
    href: 'https://discord.com/invite/YFAp2N2GfV',
    Icon: SiDiscord,
  },
  // Website intentionally omitted — mainstage.win currently serves this
  // same webapp, so linking it from here is circular. Reintroduce with a
  // separate marketing/landing URL when one exists.
  {
    label: 'X (Twitter)',
    href: '',
    Icon: SiX,
  },
  {
    label: 'YouTube',
    href: '',
    Icon: SiYoutube,
  },
];

type Props = {
  className?: string;
  variant?: 'chips' | 'subtle';
};

export function SocialLinks({ className, variant = 'chips' }: Props) {
  const visible = SOCIALS.filter((s) => s.href);
  if (visible.length === 0) return null;

  // Tap targets are kept at 40px square in both variants so the project's
  // mobile tap-target rule holds whether the row is the page's primary CTA
  // (chips) or a secondary funnel under the download (subtle).
  const chipClasses =
    variant === 'chips'
      ? 'border border-border/60 bg-card/60 hover:bg-card hover:border-primary/40'
      : 'bg-foreground/5 hover:bg-foreground/10';

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {visible.map(({ label, href, Icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className={cn(
            'h-10 w-10 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors',
            chipClasses,
          )}
        >
          <Icon className="h-5 w-5" />
        </a>
      ))}
    </div>
  );
}
